import "./QualityCheckDetail.scss"
import { useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { t } from "i18next"
import { Button } from "devextreme-react"
import { AnswerDto, Question } from "../../checklist/model/ChecklistDtos"
import QualityCheckApi from "../api/qualityCheckApi"
import QualityCheckDetailsTop from "../component/detail/QualityCheckDetailsTop"
import Category from "../component/detail/Category"
import { QualityCheckDetailDto, QuestionAnswerDto } from "../model/qualityCheckDtos"
import { ActionBar, Card, LoadIndicator, Right } from "../../../core/components"

interface GroupedByCategoryDto {
    category: any
    items: QuestionAnswerDto[]
};

export default function QualityCheckDetail() {
    const { id } = useParams()
    const [details, setDetails] = useState<QualityCheckDetailDto | undefined>(undefined)
    const [questionAnswers, setQuestionAnswers] = useState<GroupedByCategoryDto[] | undefined>(undefined)
    const [loading, setLoading] = useState<boolean>(false)
    const navigate = useNavigate()

    const fetchData = async () => {
        if (!id)
            return

        try {
            setLoading(true)

            const response = await QualityCheckApi.getQualityCheck(id)
            const questionAnswers = groupQuestionsToAnswersByCategory(response.checklist.questions, response.answers)

            setQuestionAnswers(questionAnswers)
            setDetails(response)
        }
        catch (error) {
            console.error("Failed to fetch quality check details:", error)
        }
        finally {
            setLoading(false)
        }
    }

    function groupQuestionsToAnswersByCategory(
        questions: Question[],
        answers: AnswerDto[]
    ): GroupedByCategoryDto[] {
        const groupedMap = new Map<any, QuestionAnswerDto[]>();

        questions.forEach((question) => {
            const answer = answers?.find(
                (a) => a.question.idChecklistQuestion === question.idChecklistQuestion
            );

            const dto: QuestionAnswerDto = {
                category: question.category,
                question,
                answer,
            };

            const categoryKey = question.category?.name ?? t('Uncategorized');

            if (!groupedMap.has(categoryKey)) {
                groupedMap.set(categoryKey, []);
            }

            groupedMap.get(categoryKey)!.push(dto);
        });

        return Array.from(groupedMap.entries()).map(([category, items]) => ({
            category,
            items,
        }));
    }

    useEffect(() => {
        fetchData()
    }, [id])

    const goToSummary = () => {
        if (!details) return
        navigate(`/qualitychecks/${details.idQualityCheck}/summary`)
    }

    return (
        <>
            <LoadIndicator loading={loading} />

            <ActionBar>
                <Right>
                    <Button
                        className="shadow"
                        icon="textdocument"
                        text={t('Report')}
                        type="default"
                        stylingMode="contained"
                        disabled={loading}
                        onClick={goToSummary} />
                </Right>
            </ActionBar>

            <Card className="quality-check-details border-light p-1" minHeight={500}>
                {details && <QualityCheckDetailsTop details={details} />}

                {questionAnswers && <>
                    <h2>{t("Questions and Answers")}</h2>
                    {questionAnswers.map((group, index) => (
                        <Category
                            key={`category-${index}`}
                            category={group.category}
                            answers={group.items} />
                    ))}
                </>}
            </Card>
        </>
    )
}
